// THE MATERIAL SHEET A SOLUTION IS MADE ON -- its name and size, e.g. "B2 500mm X 707mm". Resolved to the
// viewer's locale and unit from the sheet's stored description columns (the same value LayoutDimension
// #description(unit:) returns server-side), falling back to mm for a sheet that carries no column for the chosen
// unit.
//
// WHICH sheet that is depends on the solution's strategy, which is why this is a shared function and not a field
// to read. For the FOLDING strategy the solution's own material_layout_dimension IS the real board, so it names
// it. For the WITHOUT-FOLDING strategy (carton_lamination, corrugated/flexo) that field holds the
// unconstrained-sheet marker -- LayoutDimension::UNCONSTRAINED_SHEET_MM, the 100,000,000mm dummy board
// SolutionFinderWithoutFolding#large_dummy_dimension installs -- and the REAL board, used as delivered, is the
// die-cutting layout instead.
//
// Which strategy it is comes from `solution_without_folding`, the flag the SAME finder sets on the solution,
// rather than from comparing the width against a copy of the marker's size: the flag and the board are set
// together server-side, so the flag cannot drift out of step with it, whereas a duplicated literal silently could
// (the PR #5230 failure mode).
//
// THE TWO MEASUREMENTS, SWAPPED IN THE STRING. Everything else about the label survives that way: the sheet's
// name where it has one ("B2 500mm X 707mm"), the unit, and the exact formatting the server wrote -- "500" on
// one row and "500.0" on the next, and the fractional inches ("9+215/256in"), none of which rebuilding the label
// from width and height would keep. A string this does not recognise (an inch description whose fractional
// values were never filled in reads "in X in") is handed back exactly as it came.
//
// Lives here rather than beside its caller because it is a description-format concern, not a picture one: the
// priced view's picture table turns the label along with the PICTURE it draws
// (FrankSolutionSvgTable#description_for, PR #5608), which is now the only surface that turns a label at all --
// the solve turns the SHEET before margining it (Frank::ResolvesMachineMargins#sheet_as_run), so a sheet's stored
// description already names it the way the machine runs it.
export const swappedMeasurements = (description) => (
  description.replace(/(\d[\d.+/]*\s*(?:mm|in))(\s*X\s*)(\d[\d.+/]*\s*(?:mm|in))/i, '$3$2$1')
);

// Null when there is no sheet to name, so a caller draws nothing rather than an empty label.
//
// NAMED AS IT IS STORED, which IS the way it is run. A sheet used to be stored in whichever orientation its
// finder formed it -- a flexo roll piece standing up, "bobbin width x cut length" -- while the machine took it
// the other way round, so naming the stored orientation left the label and the picture describing two different
// pieces of board (a sheet made 1380mm across the bed, labelled "1380mm X 950mm", i.e. 1380mm TALL, because the
// description column is written HEIGHT FIRST). This swapped the two measurements whenever the server said
// `runs_turned`.
//
// It no longer has to. The solve TURNS THE SHEET ITSELF before margining it
// (Frank::ResolvesMachineMargins#sheet_as_run -- a sheet has 2 sides, 1 top and 1 bottom, which only lines up
// with a bed once the sheet is the right way round), so the stored orientation IS the run orientation and the
// description already reads the sheet as it is made.
export const materialSheetLabel = (diecuttingResult, materialLayoutDimension) => {
  const withoutFolding = Boolean(diecuttingResult && diecuttingResult.solution_without_folding);
  const sheet = withoutFolding ? diecuttingResult.layout_dimension : materialLayoutDimension;
  if (!sheet) { return null; }

  const locale = window.locale === 'en' ? 'en' : 'tr';
  return sheet[`description_${locale}_${window.user_unit}`] || sheet[`description_${locale}_mm`] || null;
};
